import {
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from "react";
import { RouteBodyStateContext, type Cell, type RouteBodyValue } from "#route-body-state-context";
function useRouteBodyCell<T extends RouteBodyValue>(key: string, initial: T | (() => T)): Cell<T> {
  const owner = useContext(RouteBodyStateContext);
  return useMemo(() => {
    const scopedKey = `${owner?.prefix ?? ""}/${key}`;
    const previous = owner?.snapshot.get(scopedKey);
    if (previous !== undefined) {
      // A call site's stable key always stores its own value type.
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      return previous as Cell<T>;
    }
    const cell: Cell<T> = {
      current: typeof initial === "function" ? initial() : initial,
      listeners: new Set(),
    };
    owner?.snapshot.set(scopedKey, cell);
    return cell;
    // Like useState, initial is read only when this state identity is created.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial is read only when this scoped state cell is created
  }, [key, owner]);
}

export function useRouteBodyState(
  key: string,
  initial: boolean | (() => boolean),
): [boolean, Dispatch<SetStateAction<boolean>>];
export function useRouteBodyState(
  key: string,
  initial: number | (() => number),
): [number, Dispatch<SetStateAction<number>>];
export function useRouteBodyState(
  key: string,
  initial: string | (() => string),
): [string, Dispatch<SetStateAction<string>>];
export function useRouteBodyState<T extends RouteBodyValue>(
  key: string,
  initial: T | (() => T),
): [T, Dispatch<SetStateAction<T>>];
export function useRouteBodyState<T extends RouteBodyValue>(
  key: string,
  initial: T | (() => T),
): [T, Dispatch<SetStateAction<T>>] {
  const cell = useRouteBodyCell(key, initial);
  const subscribe = useCallback(
    (listener: () => void) => {
      cell.listeners.add(listener);
      return () => {
        cell.listeners.delete(listener);
      };
    },
    [cell],
  );
  const getSnapshot = () => cell.current;
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (next) => {
      const value = typeof next === "function" ? next(cell.current) : next;
      if (Object.is(value, cell.current)) {
        return;
      }
      cell.current = value;
      for (const listener of cell.listeners) {
        listener();
      }
    },
    [cell],
  );
  return [value, setValue];
}

/** Event-written scalar drafts/offsets persist without a React render for each keystroke. */
export function useRouteBodyRef(key: string, initial: boolean): RefObject<boolean>;
export function useRouteBodyRef(key: string, initial: number): RefObject<number>;
export function useRouteBodyRef(key: string, initial: string): RefObject<string>;
export function useRouteBodyRef<T extends RouteBodyValue>(key: string, initial: T): RefObject<T>;
export function useRouteBodyRef<T extends RouteBodyValue>(key: string, initial: T): RefObject<T> {
  return useRouteBodyCell(key, initial);
}

/** A virtualized list's first visible row and that row's top relative to the viewport top. */
export type RouteBodyListScrollAnchor = { key: string; viewOffset: number };

/**
 * Restore a virtualized list's position without retaining its rows. A raw pixel offset cannot be
 * replayed into a fresh list, whose unmeasured rows only have estimated heights, so the snapshot is
 * a row key plus that row's offset from the viewport top. `initialAnchor` is read once, on mount.
 *
 * `contentKey` names what the list shows (a filter, a grade). Mounting with a different one
 * discards the saved anchor, so switching content away and back opens at the list's own default
 * position; only a remount of the same content (returning from a pushed page) restores it.
 */
export function useRouteBodyListScrollAnchor(
  key: string,
  { contentKey = "" }: { contentKey?: string } = {},
) {
  const anchorContentKey = useRouteBodyRef(`${key}:anchor-content-key`, contentKey);
  const anchorKey = useRouteBodyRef<string | null>(`${key}:anchor-key`, null);
  const anchorViewOffset = useRouteBodyRef(`${key}:anchor-view-offset`, 0);
  const [initialAnchor] = useState<RouteBodyListScrollAnchor | null>(() =>
    anchorKey.current === null || anchorContentKey.current !== contentKey
      ? null
      : { key: anchorKey.current, viewOffset: anchorViewOffset.current },
  );
  useLayoutEffect(() => {
    if (anchorContentKey.current !== contentKey) {
      anchorContentKey.current = contentKey;
      anchorKey.current = null;
    }
  }, [anchorContentKey, anchorKey, contentKey]);
  const saveAnchor = useCallback(
    (anchor: RouteBodyListScrollAnchor) => {
      anchorKey.current = anchor.key;
      anchorViewOffset.current = anchor.viewOffset;
    },
    [anchorKey, anchorViewOffset],
  );
  return { initialAnchor, saveAnchor };
}

/** Restore the native caret without retaining the editor or bringing up its keyboard. */
export function useRouteBodyEditorSelection(
  inputRef: RefObject<{ setSelection: (start: number, end: number) => void } | null>,
  key: string,
) {
  const start = useRouteBodyRef<number | null>(`${key}:selection-start`, null);
  const end = useRouteBodyRef<number | null>(`${key}:selection-end`, null);
  const [initialSelection] = useState(() => ({ end: end.current, start: start.current }));
  const [restored] = useState(() => ({ input: inputRef.current }));
  const onChangeSelection = useCallback(
    (selection: { end: number; start: number }) => {
      start.current = selection.start;
      end.current = selection.end;
    },
    [end, start],
  );
  const onLayout = useCallback(() => {
    const input = inputRef.current;
    if (input === null || restored.input === input) {
      return;
    }
    restored.input = input;
    if (initialSelection.start !== null && initialSelection.end !== null) {
      input.setSelection(initialSelection.start, initialSelection.end);
    }
  }, [initialSelection, inputRef, restored]);
  return { onChangeSelection, onLayout };
}
